import type { EventItem, EventOccurrence, OccurrenceTierInfo, ScheduleType } from '../types';

const IST = 'Asia/Kolkata';

function num(v: unknown, fallback = 0): number {
  const n = typeof v === 'string' ? Number.parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function normalizeOccurrenceTier(raw: any): OccurrenceTierInfo {
  return {
    seatCategoryId: String(raw.seatCategoryId ?? raw.seat_category_id),
    name: String(raw.name ?? ''),
    description: raw.description ?? undefined,
    colorCode: raw.colorCode ?? raw.color_code ?? null,
    price: num(raw.price),
    totalSeats: num(raw.totalSeats ?? raw.total_seats),
    availableSeats: num(raw.availableSeats ?? raw.available_seats),
    isSoldOut: Boolean(raw.isSoldOut ?? raw.is_sold_out),
  };
}

/** Normalize an occurrence row from the API (camelCase or snake_case). */
export function normalizeOccurrence(raw: any): EventOccurrence {
  const tiers = raw.tiers;
  return {
    id: String(raw.id),
    eventId: raw.eventId != null || raw.event_id != null ? String(raw.eventId ?? raw.event_id) : undefined,
    startsAt: raw.startsAt ?? raw.starts_at,
    endsAt: raw.endsAt ?? raw.ends_at ?? null,
    doorOpensAt: raw.doorOpensAt ?? raw.door_opens_at ?? null,
    label: raw.label ?? null,
    status: raw.status ?? 'scheduled',
    salesStatus: (raw.salesStatus ?? raw.sales_status ?? 'open') as EventOccurrence['salesStatus'],
    priceFrom: raw.priceFrom != null || raw.price_from != null ? num(raw.priceFrom ?? raw.price_from) : null,
    availableSeats: num(raw.availableSeats ?? raw.available_seats),
    tiers: Array.isArray(tiers) ? tiers.map(normalizeOccurrenceTier) : undefined,
  };
}

/** Calendar date (YYYY-MM-DD) of an instant, in IST - the key days are grouped by. */
export function occurrenceDayKey(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: IST });
}

/** "8 PM" / "10:30 AM" in IST. */
export function formatSlotTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: IST,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const minute = get('minute');
  return `${get('hour')}${minute === '00' ? '' : `:${minute}`} ${get('dayPeriod').toUpperCase()}`;
}

/** "Sat 2 Nov" in IST. */
export function formatDayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const wd = d.toLocaleDateString('en-US', { timeZone: IST, weekday: 'short' });
  const day = d.toLocaleDateString('en-US', { timeZone: IST, day: 'numeric' });
  const mon = d.toLocaleDateString('en-US', { timeZone: IST, month: 'short' });
  return `${wd} ${day} ${mon}`;
}

/** "Sat 2 Nov · 8 PM" - what the cart line, checkout and the ticket show. */
export function formatOccurrenceWhen(iso: string, label?: string | null): string {
  const base = `${formatDayLabel(iso)} · ${formatSlotTime(iso)}`;
  return label ? `${base} (${label})` : base;
}

export interface OccurrenceDay {
  key: string;
  iso: string;
  occurrences: EventOccurrence[];
  /** Every slot that day is sold out / past / cancelled. */
  unavailable: boolean;
}

export function isBookableOccurrence(o: EventOccurrence): boolean {
  return o.salesStatus === 'open';
}

/** Group upcoming occurrences by IST calendar day, in order. */
export function groupOccurrencesByDay(occurrences: EventOccurrence[]): OccurrenceDay[] {
  const byDay = new Map<string, EventOccurrence[]>();
  [...occurrences]
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())
    .forEach((o) => {
      const key = occurrenceDayKey(o.startsAt);
      byDay.set(key, [...(byDay.get(key) ?? []), o]);
    });
  return Array.from(byDay.entries()).map(([key, list]) => ({
    key,
    iso: list[0].startsAt,
    occurrences: list,
    unavailable: list.every((o) => !isBookableOccurrence(o)),
  }));
}

/** Events with more than one occurrence use the day/slot picker; single-date events keep today's flow. */
export function isMultiOccurrence(
  event: Pick<EventItem, 'occurrenceCount' | 'occurrences'>
): boolean {
  return (event.occurrenceCount ?? event.occurrences?.length ?? 1) > 1;
}

/** "2–4 Nov" for a run on different days, otherwise a single day label. */
function rangeLabel(startIso: string, endIso: string): string {
  if (occurrenceDayKey(startIso) === occurrenceDayKey(endIso)) return formatDayLabel(startIso);
  const s = new Date(startIso);
  const e = new Date(endIso);
  const sMon = s.toLocaleDateString('en-US', { timeZone: IST, month: 'short' });
  const eMon = e.toLocaleDateString('en-US', { timeZone: IST, month: 'short' });
  const sDay = s.toLocaleDateString('en-US', { timeZone: IST, day: 'numeric' });
  const eDay = e.toLocaleDateString('en-US', { timeZone: IST, day: 'numeric' });
  return sMon === eMon ? `${sDay}–${eDay} ${eMon}` : `${sDay} ${sMon} – ${eDay} ${eMon}`;
}

/** Discovery-card date label: null for single-date events (keep today's label). */
export function scheduleDateLabel(
  event: Pick<EventItem, 'occurrenceCount' | 'occurrences' | 'scheduleType' | 'eventDate' | 'eventEndDate'>
): string | null {
  if (!isMultiOccurrence(event) || !event.eventDate) return null;
  if (event.scheduleType === 'recurring') return 'Multiple dates';
  if (event.eventEndDate) return rangeLabel(event.eventDate, event.eventEndDate);
  return 'Multiple dates';
}

const BADGES: Partial<Record<ScheduleType, string>> = {
  multi_slot: 'Multiple slots',
  multi_day: 'Multi-day',
  recurring: 'Recurring',
  season_pass: 'Season pass',
};

/** Small badge for discovery cards; none for ordinary single-date events. */
export function scheduleBadge(event: Pick<EventItem, 'scheduleType'>): string | null {
  if (!event.scheduleType || event.scheduleType === 'single') return null;
  return BADGES[event.scheduleType] ?? null;
}
